import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener('plan-change', this.updatePlanPrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener('plan-change', this.updatePlanPrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }
  };

  updatePlanPrice = (event) => {
    const { planType, yearlyTotal, yearlyPerItem, monthlyPrice } = event.detail || {};
    if (!planType) return;

    const priceContainer = this.querySelector('[ref="priceContainer"]');
    if (!priceContainer) return;

    const rawPrice = planType === 'yearly'
      ? (yearlyPerItem ?? yearlyTotal)
      : monthlyPrice;
    const priceCents = parseInt(rawPrice, 10);
    if (!priceCents) return;

    const formattedPrice = this.formatMoney(priceCents);
    const priceEl = priceContainer.querySelector('.price') || priceContainer;
    if (priceEl && priceEl.textContent !== formattedPrice) {
      priceEl.textContent = formattedPrice;
    }
  };

  formatMoney(cents) {
    // @ts-ignore - Shopify global may not be typed
    const amount = (cents / 100).toFixed(0);
    const cleaned = amount.replace(/\.00$/, '');
    return `$${cleaned}`;
    if (typeof window.Shopify?.formatMoney === 'function') {
      // @ts-ignore
      return window.Shopify.formatMoney(cents);
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
